/** Parsiranje adrese iz slobodnog teksta koji kupac pošalje porukom (Viber, Instagram,
 *  Messenger...) u polja forme za naplatu. Sve što je ovdje je čista logika bez mreže —
 *  AI poziv je odvojen (`lib/ocr/openrouter.callTextModel`), a ovdje su prompt,
 *  parsiranje odgovora i poklapanje grada s poštanskim brojem iz ziplist-a. */

import zipList from "@/lib/ziplist";
import { normalizeText } from "@/lib/fuzzy";

export type ParsedAddress = {
  first_name: string;
  last_name: string;
  address_1: string;
  city: string;
  postcode: string;
  phone: string;
  email: string;
};

export const EMPTY_PARSED: ParsedAddress = {
  first_name: "",
  last_name: "",
  address_1: "",
  city: "",
  postcode: "",
  phone: "",
  email: "",
};

export const MAX_INPUT_LEN = 2000;

export const ADDRESS_PROMPT = `Iz poruke kupca izvuci podatke za dostavu u Bosni i Hercegovini.

Vrati ISKLJUČIVO JSON objekat s tačno ovim ključevima:
{"first_name":"","last_name":"","address_1":"","city":"","postcode":"","phone":"","email":""}

Pravila:
- first_name = ime, last_name = prezime. Ako je napisano "Prezime Ime", prepoznaj koje je ime a koje prezime po tipičnim bosanskim/hrvatskim/srpskim imenima.
- address_1 = ulica i broj (npr. "Zmaja od Bosne 4"). Bez grada i bez poštanskog broja.
- city = samo naziv grada/mjesta, bez poštanskog broja.
- postcode = poštanski broj ako je naveden, inače prazan string.
- phone = broj telefona samo cifre i eventualni vodeći +, bez razmaka.
- email = email ako postoji, inače prazan string.
- Popravi očiglednu dijakritiku i velika/mala slova (npr. "sarajevo" -> "Sarajevo").
- Polje koje ne možeš pouzdano pročitati ostavi kao prazan string. Ništa ne izmišljaj.

Poruka kupca:
`;

export function buildAddressPrompt(text: string): string {
  return ADDRESS_PROMPT + text.slice(0, MAX_INPUT_LEN);
}

/** Model zna umotati JSON u ```json ograde ili dodati tekst okolo — uzmi prvi objekat. */
export function parseAddressResponse(raw: string): ParsedAddress {
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start === -1 || end <= start) {
    throw new Error("Model nije vratio JSON");
  }
  let data: any;
  try {
    data = JSON.parse(raw.slice(start, end + 1));
  } catch {
    throw new Error("Model nije vratio ispravan JSON");
  }

  const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const parsed: ParsedAddress = {
    first_name: str(data.first_name),
    last_name: str(data.last_name),
    address_1: str(data.address_1),
    city: str(data.city),
    postcode: str(data.postcode),
    phone: normalizePhone(str(data.phone)),
    email: str(data.email).toLowerCase(),
  };

  const resolved = resolveCity(parsed.city, parsed.postcode);
  parsed.city = resolved.city;
  parsed.postcode = resolved.postcode;
  return parsed;
}

/** BA brojevi: +387 61 123 456 / 00387... / 061-123-456 -> 061123456. */
export function normalizePhone(input: string): string {
  if (!input) return "";
  let digits = input.replace(/[^\d+]/g, "");
  digits = digits.replace(/^\+?00387/, "0").replace(/^\+387/, "0");
  digits = digits.replace(/\+/g, "");
  // Strani brojevi (npr. +385...) ostaju kakvi jesu, samo bez razmaka.
  return digits;
}

type ZipEntry = { value: string; label: string };

/** "Agino Selo - 78209" -> "Agino Selo" */
/** Naziv grada bez poštanskog broja iz ziplist labele ("Banja Luka - 78000" → "Banja Luka"). */
export function cityOfLabel(label: string): string {
  return label.replace(/\s*-\s*[A-Z]{0,2}-?\d+$/, "").trim();
}

let cityIndex: Map<string, ZipEntry[]> | null = null;
function getCityIndex(): Map<string, ZipEntry[]> {
  if (cityIndex) return cityIndex;
  const index = new Map<string, ZipEntry[]>();
  for (const entry of zipList as ZipEntry[]) {
    const key = normalizeText(cityOfLabel(entry.label));
    const bucket = index.get(key);
    if (bucket) bucket.push(entry);
    else index.set(key, [entry]);
  }
  cityIndex = index;
  return index;
}

/**
 * Uskladi grad i poštanski broj s ziplist-om — grad u formi je combobox vezan za
 * ziplist, pa naziv mora biti tačno onakav kakav je u listi da bi se prikazao kao
 * odabran. Ako se ne može poklopiti, vraća ono što je model dao (korisnik ispravi ručno).
 */
export function resolveCity(city: string, postcode: string): { city: string; postcode: string } {
  const index = getCityIndex();
  const byName = city ? index.get(normalizeText(city)) : undefined;

  if (byName?.length) {
    // Više mjesta istog imena — poštanski broj iz poruke odlučuje.
    const exact = postcode ? byName.find((e) => e.value === postcode) : undefined;
    const chosen = exact ?? byName[0]!;
    return { city: cityOfLabel(chosen.label), postcode: chosen.value };
  }

  if (postcode) {
    const byZip = (zipList as ZipEntry[]).find((e) => e.value === postcode);
    if (byZip) return { city: cityOfLabel(byZip.label), postcode: byZip.value };
  }

  return { city, postcode };
}
